import { Component, forwardRef, Input, OnDestroy } from '@angular/core';
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  AbstractControl,
  ReactiveFormsModule,
  FormsModule,
  FormControl,
} from '@angular/forms';
import { BeyFieldErrorMessageComponent } from '../bey-field-error-message/bey-field-error-message.component';
import { NgIf, NgClass } from '@angular/common';
import { Subscription } from 'rxjs';
import { FormUtilityService } from 'src/app/modules/core/services/form-utility.service';

export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => BeyInputPinComponent),
  multi: true,
};

const noop = () => {};

@Component({
  selector: 'bey-input-pin',
  templateUrl: './bey-input-pin.component.html',
  styleUrls: ['./bey-input-pin.component.scss'],
  providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
  standalone: true,
  imports: [NgIf, ReactiveFormsModule, FormsModule, NgClass, BeyFieldErrorMessageComponent],
})
export class BeyInputPinComponent implements ControlValueAccessor, OnDestroy {
  /***
   *  Text for input field placeholder attribute
   */
  @Input()
  placeholder: string = '';

  /***
   *  Text for input field label
   */
  @Input()
  label: string = '';

  /***
   *  Input field name attribute
   */
  @Input()
  name: string = '';

  /***
   *  Boolean flag to toggle hiding/showing error
   */
  @Input()
  displayError: boolean = false;

  /***
   * Field's validation error message
   */
  @Input()
  errorMessage: string = '';

  /***
   *  Hide the validation feedback message showing under the field
   */
  @Input()
  hideValidationFeedback: boolean = false;

  /****
   * Autocomplete attribute value: Default is 'new-password'
   */
  @Input()
  autocomplete: string = 'new-password';

  /****
   * This is the type of the input field, and it's switched from password to text
   */
  type: string = 'password';

  /****
   * formControl instance
   */
  @Input()
  control: AbstractControl | FormControl;

  subs$: Subscription = new Subscription();

  /****
   *  This component supports model
   *  FORM CONTROL
   *  formControlName="idNum"
   */
  constructor(private formUtility: FormUtilityService) {}

  ngOnInit(): void {
    if (!!this.control) {
      this.subs$.add(
        this.control.valueChanges.subscribe(() => {
          this.updateErrorStatus();
        })
      );
    }
  }

  updateErrorStatus(): void {
    if (this.control) {
      this.control.markAsTouched();
      this.errorMessage = this.formUtility.getFieldErrorMessage(this.control);
      this.displayError = this.formUtility.getFieldValidation(this.control);
    }
  }

  //The internal data model
  private innerValue: any = '';
  //Placeholders for the callbacks which are later provided
  //by the Control Value Accessor
  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  get value(): any {
    return this.innerValue;
  }

  //set accessor including call the onchange callback
  set value(v: any) {
    if (v !== this.innerValue) {
      this.innerValue = v;
      this.onChangeCallback(v);
    }
  }

  //Set touched on blur
  onBlur() {
    this.onTouchedCallback();
    this.updateErrorStatus();
  }

  //From ControlValueAccessor interface
  writeValue(value: any) {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
  }

  //From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  //From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  onEyeClick(status: boolean): void {
    if (status) this.type = 'text';
    else this.type = 'password';
  }

  ngOnDestroy(): void {
    this.subs$.unsubscribe();
  }
}
